import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'src/routes/hooks/use-router';

import {Box}from '@mui/material';
import List from '@mui/material/List';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import { styled } from '@mui/material/styles';

import { fToNow } from 'src/utils/format-time';
import { useNotification } from 'src/contexts/NotificationContext';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';

// ----------------------------------------------------------------------

// Type to color mapping for notifications - using standard MUI colors
const NOTIFICATION_COLORS = {
  title: {
    bg: 'info.light',
    color: 'info.dark',
    icon: 'mdi:format-title',
    gradient: 'linear-gradient(135deg, #73e0f7 0%, #0288d1 100%)',
  },
  ffuf: {
    bg: 'warning.light',
    color: 'warning.dark',
    icon: 'mdi:folder-search',
    gradient: 'linear-gradient(135deg, #ffe57f 0%, #ffa000 100%)',
  },
  liveDomain: {
    bg: 'success.light',
    color: 'success.dark',
    icon: 'mdi:domain',
    gradient: 'linear-gradient(135deg, #81c784 0%, #2e7d32 100%)',
  },
  nuclei: {
    bg: 'error.light',
    color: 'error.dark',
    icon: 'mdi:security',
    gradient: 'linear-gradient(135deg, #ff8a80 0%, #d32f2f 100%)',
  },
  aclBypass: {
    bg: 'warning.light',
    color: 'warning.dark',
    icon: 'mdi:shield-alert',
    gradient: 'linear-gradient(135deg, #ffcc80 0%, #ef6c00 100%)',
  },
  vulnerability: {
    bg: 'error.light',
    color: 'error.dark',
    icon: 'mdi:bug',
    gradient: 'linear-gradient(135deg, #ef9a9a 0%, #c62828 100%)',
  },
  secrets: {
    bg: 'error.light',
    color: 'error.dark',
    icon: 'mdi:key-chain-variant',
    gradient: 'linear-gradient(135deg, #ff8a65 0%, #bf360c 100%)',
  },
  default: {
    bg: 'grey.300',
    color: 'grey.800',
    icon: 'mdi:bell',
    gradient: 'linear-gradient(135deg, #e0e0e0 0%, #757575 100%)',
  },
};

// Change the header styling to remove background color/shading
const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export default function NotificationsPopover() {
  const { 
    notifications, 
    unreadCount, 
    hasNewNotifications,
    acknowledgeNotifications,
    clearAllNotifications,
    lastAcknowledgedTime
  } = useNotification();
  
  const [open, setOpen] = useState(null);

  // Notification bell pulsing animation when there are new notifications
  const [isPulsing, setIsPulsing] = useState(false);

  // Start pulsing animation when there are new notifications
  useEffect(() => {
    if (hasNewNotifications) {
      setIsPulsing(true);
      
      // Stop the animation after 10 seconds
      const timer = setTimeout(() => {
        setIsPulsing(false);
      }, 10000);
      
      return () => clearTimeout(timer);
    }
    return undefined; // Explicit return for consistent-return ESLint rule
  }, [hasNewNotifications]);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleAcknowledge = () => {
    acknowledgeNotifications();
    // Optionally close the popover
    setOpen(null);
  };

  const handleClearAll = () => {
    clearAllNotifications();
    // Close the popover
    setOpen(null);
  };

  // Group notifications into categories
  const groupedNotifications = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    // If there's no last acknowledgment time, treat all notifications as new
    if (!lastAcknowledgedTime) {
      console.log('No last acknowledgment time, treating all notifications as new');
      return {
        sinceLastAcknowledged: notifications,
        recentlyAcknowledged: [],
        older: []
      };
    }
    
    // Get notifications since last acknowledgment
    const sinceLastAcknowledged = notifications.filter(n => new Date(n.createdAt) > new Date(lastAcknowledgedTime));
    
    console.log('Notifications since last acknowledged:', sinceLastAcknowledged.length);
    console.log('Last acknowledged time:', new Date(lastAcknowledgedTime).toLocaleString());
    
    // Debug unique IDs
    const allIds = notifications.map(n => n.id);
    const uniqueIds = new Set(allIds);
    if (allIds.length !== uniqueIds.size) {
      console.warn('Duplicate notification IDs detected:', 
        allIds.filter((id, index) => allIds.indexOf(id) !== index));
    }
    
    return {
      // New but haven't been acknowledged yet
      sinceLastAcknowledged,
      // Within last 24 hours (but were acknowledged)
      recentlyAcknowledged: notifications.filter(n => 
        new Date(n.createdAt) > oneDayAgo && 
        new Date(n.createdAt) <= new Date(lastAcknowledgedTime)
      ),
      // Older than 24 hours
      older: notifications.filter(n => new Date(n.createdAt) <= oneDayAgo)
    };
  }, [notifications, lastAcknowledgedTime]);

  // Return if there are any notifications in the "since last acknowledged" group
  const hasNewSinceAcknowledged = groupedNotifications.sinceLastAcknowledged.length > 0;

  return (
    <>
      <IconButton 
        color={open ? 'primary' : 'default'} 
        onClick={handleOpen}
        sx={{
          position: 'relative',
          ...(isPulsing && {
            animation: 'pulse 1.5s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)',
              },
              '50%': {
                transform: 'scale(1.1)',
                boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
              },
            },
          }),
        }}
      >
        <Badge 
          badgeContent={hasNewSinceAcknowledged ? groupedNotifications.sinceLastAcknowledged.length : (unreadCount > 0 ? unreadCount : 0)} 
          color={hasNewSinceAcknowledged ? "success" : "error"}
          invisible={!hasNewSinceAcknowledged && unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              ...(hasNewNotifications && {
                bgcolor: 'success.main',
                color: 'success.contrastText',
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
              }),
            },
          }}
        >
          <Iconify 
            width={24} 
            icon="solar:bell-bing-bold-duotone" 
            sx={{
              color: hasNewNotifications ? 'primary.main' : 'inherit',
            }}
          />
        </Badge>
      </IconButton>

      <Popover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            ml: 0.75,
            width: 380,
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: (theme) => theme.customShadows?.z24 || '0 2px 16px 0 rgba(0,0,0,0.24)',
            animation: 'fadeIn 0.3s ease-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(-10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          },
        }}
      >
        <Header>
          <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              Notifications
              {hasNewSinceAcknowledged && (
                <Typography 
                  component="span" 
                  variant="body2"
                  sx={{ 
                    ml: 1,
                    fontWeight: 'normal'
                  }}
                >
                  {groupedNotifications.sinceLastAcknowledged.length} new
                </Typography>
              )}
            </Box>
            {hasNewSinceAcknowledged && (
              <Typography
                variant="caption"
                sx={{ 
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onClick={handleAcknowledge}
              >
                Mark as seen
              </Typography>
            )}
          </Typography>
        </Header>

        <Scrollbar sx={{ height: { xs: 340, sm: 420 } }}>
          <List
            disablePadding
            subheader={false}
          >
            {/* New notifications since last acknowledged */}
            {groupedNotifications.sinceLastAcknowledged.length > 0 && (
              <>
                <Box sx={{ p: 1, backgroundColor: 'success.light' }}>
                  <Typography variant="subtitle2" color="success.dark" fontWeight="bold">
                    {!lastAcknowledgedTime ? 'Available Data' : 'New Since Last Visit'}
                  </Typography>
                </Box>
                {groupedNotifications.sinceLastAcknowledged.map((notification, index) => (
                  <NotificationItem
                    key={`${notification.id}-${index}-new`}
                    notification={notification}
                    isNew={!!lastAcknowledgedTime} // Only mark as "new" if there was a previous visit
                  />
                ))}
              </>
            )}
            
            {/* Recent but acknowledged notifications */}
            {groupedNotifications.recentlyAcknowledged.length > 0 && (
              <>
                <Box sx={{ p: 1, backgroundColor: 'background.neutral' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Recent
                  </Typography>
                </Box>
                {groupedNotifications.recentlyAcknowledged.map((notification, index) => (
                  <NotificationItem
                    key={`${notification.id}-${index}-recent`}
                    notification={notification}
                  />
                ))}
              </>
            )}
            
            {/* Older notifications */}
            {groupedNotifications.older.length > 0 && (
              <>
                <Box sx={{ p: 1, backgroundColor: 'background.neutral' }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Earlier
                  </Typography>
                </Box>
                {groupedNotifications.older.map((notification, index) => (
                  <NotificationItem
                    key={`${notification.id}-${index}-older`}
                    notification={notification}
                  />
                ))}
              </>
            )}
            
            {notifications.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box
                  component="img"
                  src="/assets/illustrations/illustration_empty_content.svg"
                  sx={{ height: 120, mx: 'auto' }}
                />
                <Typography variant="subtitle1" gutterBottom>
                  No Notifications
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  You don&apos;t have any notifications yet.
                </Typography>
              </Box>
            )}
          </List>
        </Scrollbar>

        <Box sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
          <Button 
            fullWidth 
            variant="contained"
            onClick={
              !lastAcknowledgedTime 
                ? handleAcknowledge
                : hasNewSinceAcknowledged 
                  ? handleAcknowledge 
                  : (unreadCount > 0 
                    ? handleAcknowledge 
                    : handleClearAll)
            }
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #90caf9 0%, #1976d2 100%)',
              boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
              '&:hover': { 
                boxShadow: '0 4px 12px rgba(25,118,210,0.5)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
              fontWeight: 'bold',
              py: 1,
            }}
          >
            {!lastAcknowledgedTime 
              ? 'Acknowledge All Data' 
              : (hasNewSinceAcknowledged 
                ? 'Acknowledge New Items' 
                : (unreadCount > 0 ? 'Mark All as Read' : 'Clear All'))}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    createdAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    id: PropTypes.string,
    isUnRead: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
  }),
  isNew: PropTypes.bool,
};

NotificationItem.defaultProps = {
  isNew: false,
};

function NotificationItem({ notification, isNew }) {
  const router = useRouter();
  const { handleNotificationClick } = useNotification();
  
  // Get notification colors from type with stronger fallback
  const notificationStyle = notification.type && NOTIFICATION_COLORS[notification.type] 
    ? NOTIFICATION_COLORS[notification.type] 
    : NOTIFICATION_COLORS.default;
  
  // Simplify to avoid color split issues - use fixed values for bgcolor
  const bgColorUnread = isNew ? 'rgba(46, 125, 50, 0.12)' : 'rgba(25, 118, 210, 0.08)';
  const bgColorUnreadHover = isNew ? 'rgba(46, 125, 50, 0.18)' : 'rgba(25, 118, 210, 0.15)';
  
  // We know all these values exist in the defaults
  const gradient = notificationStyle?.gradient || 'linear-gradient(135deg, #e0e0e0 0%, #757575 100%)';
  const icon = notificationStyle?.icon || 'mdi:bell';

  // Handle notification click
  const onNotificationClick = () => {
    if (handleNotificationClick) {
      handleNotificationClick(notification);
    } else {
      // Fallback navigation if handleNotificationClick is not available
      switch (notification.type) {
        case 'title':
          router.push('/new-titles');
          break;
        case 'ffuf':
          router.push('/ffuf-output');
          break;
        case 'liveDomain':
          router.push('/domain-live-stream');
          break;
        case 'nuclei':
          router.push('/nuclei-scan');
          break;
        case 'aclBypass':
          router.push('/acl-bypasses');
          break;
        case 'vulnerability':
          router.push('/vulnerability-findings');
          break;
        case 'secrets':
          router.push('/secrets-live-stream');
          break;
        default:
          router.push('/overview');
          break;
      }
    }
  };

  return (
    <ListItemButton
      onClick={onNotificationClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        ...(isNew && {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: 'linear-gradient(135deg, #81c784 0%, #2e7d32 100%)',
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
          },
          bgcolor: bgColorUnread,
          '&:hover': {
            bgcolor: bgColorUnreadHover,
            transform: 'translateX(2px)',
          },
        }),
        ...(notification.isUnRead && !isNew && {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: gradient,
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
          },
          bgcolor: bgColorUnread,
          '&:hover': {
            bgcolor: bgColorUnreadHover,
            transform: 'translateX(2px)',
          },
        }),
        ...(!notification.isUnRead && !isNew && {
          '&:hover': {
            bgcolor: 'action.hover',
            transform: 'translateX(2px)',
          },
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar 
          sx={{ 
            background: isNew ? 'linear-gradient(135deg, #81c784 0%, #2e7d32 100%)' : gradient,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Iconify icon={isNew ? 'mdi:bell-ring' : icon} width={20} height={20} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography 
            variant="subtitle2"
            sx={{ 
              color: isNew ? 'success.dark' : (notification.isUnRead ? 'text.primary' : 'text.secondary'),
              fontWeight: isNew || notification.isUnRead ? 'bold' : 'normal',
              transition: 'color 0.2s ease',
            }}
          >
            {notification.title}
            {isNew && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  display: 'inline-block',
                  bgcolor: 'success.main',
                  color: 'white',
                  fontSize: '0.65rem',
                  borderRadius: '4px',
                  px: 0.7,
                  py: 0.2,
                  lineHeight: 1,
                  verticalAlign: 'middle'
                }}
              >
                NEW
              </Box>
            )}
          </Typography>
        }
        secondary={
          <Box sx={{ mt: 0.3 }}>
            <Typography 
              component="div" 
              variant="body2" 
              sx={{ 
                color: isNew ? 'success.dark' : (notification.isUnRead ? 'text.secondary' : 'text.disabled'),
              }}
            >
              {notification.description}
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                color: 'text.disabled',
              }}
            >
              <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
              <Typography variant="caption">
                {typeof notification.createdAt === 'string' 
                  ? fToNow(new Date(notification.createdAt)) 
                  : fToNow(notification.createdAt)}
              </Typography>
            </Box>
          </Box>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  // Get notification style from type
  const notificationStyle = NOTIFICATION_COLORS[notification.type] || NOTIFICATION_COLORS.default;

  const title = notification.title;

  return {
    avatar: <Iconify icon={notificationStyle.icon} width={20} height={20} />,
    title,
    color: notificationStyle.color,
  };
}
